import {
  archiveClient,
  createClient,
  getClients,
  selectClients,
  updateClient,
} from "app/redux/clientsSlice";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getFormValues } from "app/utils/getFormValues";
import { find } from "lodash";
import { FormEvent, useEffect, useState } from "react";
import { useQuery } from "app/utils/useQuery";
import { useNavigate } from "react-router-dom";
import { Path } from "app/path";
import { selectActiveEmployees } from "app/redux/employeesSlice";
import http from "app/requests/axiosInstance";

export const useClientDrawer = ({
  clientId,
  setCloseParent,
  fetchClients,
}: {
  clientId?: number;
  setCloseParent?: (value: boolean) => void;
  fetchClients?: () => void;
}) => {
  const query = useQuery();
  const navigate = useNavigate();
  const { clients } = useSelector(selectClients);
  const employees = useSelector(selectActiveEmployees);
  const managersIdsToFilterParam = query.get("managerIdsToFilter");
  const dispatch = useDispatch();
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    null
  );
  const [client, setClient] = useState<any | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [newNoteContent, setNewNoteContent] = useState<string>("");
  const [showNoteTextArea, setShowNoteTextArea] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isArchivingClient, setIsArchivingClient] = useState<boolean>(false);
  const [managerIdsWhenCreating, setManagerIdsWhenCreating] = useState<
    number[]
  >([]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // setIsCreatingClient(true);
    const { firstName, lastName, email, phone, gender, notes,address,dob } = getFormValues(
      event.target as HTMLFormElement
    );

    // Proper validation
    if (!firstName) {
      toast.error("First name is required");
      setIsCreatingClient(false);
      return;
    }

    if (!lastName) {
      toast.error("Last name is required");
      setIsCreatingClient(false);
      return;
    }

    // Email is optional: family members often share one phone and have no email of their own
    if (!phone) {
      toast.error("Phone is required");
      setIsCreatingClient(false);
      return;
    }

    const payload = {
      name: `${firstName} ${lastName}`,
      email,
      phone,
      gender,
      notes,
      address,dob,
    };

    try {
      const client = await dispatch(createClient(payload) as any).unwrap();

      if (profileImageFile) {
        await handleAvatarUpload(client.id); // Handle avatar upload after creating client
      }

      setIsCreatingClient(false);
      setCloseParent?.(false);
      toast.success("Client created successfully");
    } catch (error:any) {
      setIsCreatingClient(false);
      console.error(error);
      toast.error(error.data.error ?? "Failed to create client");
    }
  };

  const onArchiveClient = async (clientToBeDeletedId: number) => {
    try {
      setIsArchivingClient(true);
      await dispatch(archiveClient(clientToBeDeletedId) as any).unwrap();
      toast.success("Client deleted successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to deleted client");
    } finally {
      setIsArchivingClient(false);
      setShowDeleteModal(false);
      setCloseParent?.(true);
      query.delete("clientId");
      navigate(Path.CLIENTS);
    }
  };

  const handleAvatarUpload = async (clientId?: number, file?: any) => {
    const imageFileToUser = file || profileImageFile;

    if (clientId && imageFileToUser) {
      const formData = new FormData();
      formData.append("client[avatar]", imageFileToUser);

      try {
        await dispatch(
          updateClient({
            id: clientId,
            avatar: imageFileToUser,
          }) as any
        ).unwrap();
        toast.success("Profile image updated successfully");
      } catch (error: any) {
        console.error(
          "Error updating profile image:",
          error.response || error.message || error
        );
        toast.error("Failed to update profile image");
      }
    }
  };

  const onUserUpdate = async ({
    key,
    value,
  }: {
    key: string;
    value: string | number[];
  }) => {
    setIsCreatingClient(true);

    let finalKey = key;
    let finalValue = value;
    if (key === "firstName") {
      finalKey = "name";
      finalValue = `${value} ${client!.name.split(" ")[1]}`;
    } else if (key === "lastName") {
      finalKey = "name";
      finalValue = `${client!.name.split(" ")[0]} ${value}`;
    }

    if (key === "phone" && finalValue === "") {
      toast.error("A phone number is required");
      setIsCreatingClient(false);
      return;
    }

    try {
      await dispatch(
        updateClient({
          id: client!.id,
          [finalKey]: finalValue,
        } as any) as any
      ).unwrap();

      if (profileImageFile) {
        await handleAvatarUpload(client!.id); // Handle avatar upload after updating client
      }

      setIsCreatingClient(false);
      toast.success("Client updated successfully");
    } catch (error) {
      setIsCreatingClient(false);
      console.error(error);
      toast.error("Failed to update client");
    }
  };

  const handleProfileImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    submitNow = false
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setProfileImagePreview(imageUrl);
      setProfileImageFile(file); // Store the file in state
    }
    if (submitNow) {
      await handleAvatarUpload(client?.id, file);
    }
  };

  const addNewNote = async () => {
    if (client) {
      try {
        await dispatch(
          updateClient({
            id: client.id,
            note: newNoteContent,
          } as any) as any
        ).unwrap();
        toast.success("Note added successfully");
      } catch (error) {
        console.error(error);
        toast.error("Failed to add note");
      }
    }
  };

  const onManagerUpdate = async (managerIds: number[]) => {
    await onUserUpdate({ key: "managerIds", value: managerIds });
    if (managersIdsToFilterParam) {
      await fetchClients?.();
    }
  };

  useEffect(() => {
    return () => {
      if (profileImagePreview) URL.revokeObjectURL(profileImagePreview);
    };
  }, [profileImagePreview]);

  useEffect(() => {
    if (clientId) {
      const foundClient = find(clients, { id: clientId });
      if (!foundClient) {
        query.delete("clientId");
        setCloseParent?.(true);
        navigate(`${Path.CLIENTS}?${query.toString()}`);
      }
      setClient(foundClient);
    }

    return () => {
      setShowNoteTextArea(false);
    };
  }, [clientId]);

  const [sales, setSales] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);

  useEffect(() => {
    if (clientId) {
      const fetchHistory = async () => {
        setIsLoadingHistory(true);
        try {
          const res = await http.get(`/v1/sales?client_id=${clientId}&per_page=1000`);
          setSales(res.data.sales || []);
        } catch (error) {
          console.error("Error fetching client sales:", error);
        } finally {
          setIsLoadingHistory(false);
        }
      };
      fetchHistory();
    } else {
      setSales([]);
    }
  }, [clientId]);

  return {
    onSubmit,
    isCreatingClient,
    onUserUpdate,
    clients,
    client,
    profileImagePreview,
    handleProfileImageChange,
    showNoteTextArea,
    setShowNoteTextArea,
    addNewNote,
    newNoteContent,
    setNewNoteContent,
    onArchiveClient,
    setShowDeleteModal,
    showDeleteModal,
    isArchivingClient,
    employees,
    setManagerIdsWhenCreating,
    onManagerUpdate,
    sales,
    isLoadingHistory,
  };
};
